import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Claim } from '../../types/report';
import type { ReportChatMsg } from '../../state/reportPanel';
import { useReportPanel, CHAT_UNSCOPED } from '../../state/reportPanel';
import { askReport, getReportChatHistory } from '../../services/reportService';

interface ReportChatPanelProps {
  reportId: string;
  claims: Record<string, Claim>;
}

/** Suggestion chips seeded from the scoped claim (mock's chatSuggestions). Falls
 * back to generic report questions when unscoped. Derived from the claim's own
 * title so it works for any study, no hardcoded per-report copy. */
function suggestionsFor(claim: Claim | undefined): string[] {
  if (!claim) {
    return [
      'Which segment is the biggest?',
      'What is the central tension in this category?',
      'Which segment should we prioritise and why?',
    ];
  }
  const t = claim.header.title || 'this number';
  return [
    `What does "${t}" mean and how was it measured?`,
    `How does "${t}" compare across the segments?`,
    `What should we do about "${t}"?`,
  ];
}

export function ReportChatPanel({ reportId, claims }: ReportChatPanelProps) {
  // messages + busy live in context so a conversation (and an in-flight request)
  // survive this sub-panel unmounting on a Details↔Chat tab switch or a
  // citation→drill navigation. Writing back through the (always-mounted) parent
  // is safe even after THIS component unmounts — so no mounted-ref guards.
  const {
    activeClaimId, open,
    chatMessages: messages, setChatMessages: setMessages,
    chatBusy: busy, setChatBusy: setBusy,
  } = useReportPanel();
  const scopeClaimId = activeClaimId && activeClaimId !== CHAT_UNSCOPED ? activeClaimId : undefined;
  const scopeClaim = scopeClaimId ? claims[scopeClaimId] : undefined;

  const [input, setInput] = useState('');
  const suggestions = useMemo(() => suggestionsFor(scopeClaim), [scopeClaim]);
  const listRef = useRef<HTMLDivElement>(null);

  // Load the persisted conversation once, when the panel opens with no messages
  // yet in context (fresh open / after reload). One thread per report. Maps the
  // backend {role, content, metadata} shape into the local ChatMsg shape.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current || messages.length > 0) return;
    loadedRef.current = true;
    (async () => {
      const raw = await getReportChatHistory(reportId);
      if (raw.length === 0) return;
      const restored: ReportChatMsg[] = raw.map((m) => {
        const meta = (m.metadata as Record<string, unknown>) || {};
        return {
          role: m.role === 'user' ? 'user' : 'ai',
          content: String(m.content ?? ''),
          citedClaimIds: meta.citedClaimIds as string[] | undefined,
          followUps: meta.followUps as string[] | undefined,
          adjacent: meta.adjacent as string[] | undefined,
        };
      });
      setMessages((prev) => (prev.length > 0 ? prev : restored));
    })();
  }, [reportId, messages.length, setMessages]);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  async function send(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: q },
      { role: 'ai', content: 'Thinking…', pending: true }]);
    setInput('');
    scrollDown();
    try {
      const res = await askReport(reportId, q, scopeClaimId);
      let content: string;
      let cited: string[] | undefined;
      const followUps: string[] | undefined = res.followUps;
      let adjacent: string[] | undefined;
      if (res.mode === 'decline') {
        // graceful decline: name what's missing (or point to Survey Chat for raw
        // data), and offer adjacent things this report CAN show.
        content = res.declineReason || "That isn't something this study covers.";
        adjacent = res.adjacent;
      } else {
        // 'claims' — an inline answer cited to the report's own figures.
        content = res.answer || 'No answer.';
        cited = res.citedClaimIds;
      }
      setMessages((m) => {
        if (!m.length || !m[m.length - 1].pending) return m;
        return [...m.slice(0, -1), { role: 'ai', content, citedClaimIds: cited, followUps, adjacent }];
      });
    } catch {
      setMessages((m) => {
        if (!m.length || !m[m.length - 1].pending) return m;
        return [...m.slice(0, -1), { role: 'ai', content: 'Sorry — something went wrong. Please try again.' }];
      });
    } finally {
      setBusy(false);
      scrollDown();
    }
  }

  return (
    <div className="report-chat" data-testid="report-chat">
      <div className="chat-scope">
        {scopeClaim
          ? <>Asking about <strong>{scopeClaim.header.title}</strong></>
          : <>Ask anything about this report — answers are drawn from its data and cited.</>}
      </div>

      <div className={`chat-messages${messages.length === 0 ? ' empty' : ''}`} ref={listRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`chat-msg ${msg.role}`}>
            {msg.role === 'ai'
              ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
              : msg.content}
            {msg.citedClaimIds && msg.citedClaimIds.length > 0 && (
              <div className="chat-msg-cites">
                {/* every cited claim is clickable — DrillPanel lazy-fetches ones
                    not inlined in the doc, so don't drop them. Label falls back
                    to the id when the title isn't available locally. */}
                {msg.citedClaimIds.map((id) => (
                  <button key={id} type="button" className="chat-cite" disabled={busy}
                    onClick={() => open(id)}>
                    ✦ {claims[id]?.header.title ?? id}
                  </button>
                ))}
              </div>
            )}
            {msg.adjacent && msg.adjacent.length > 0 && (
              <div className="chat-adjacent">
                <div className="chat-adjacent-label">What this report can show</div>
                {msg.adjacent.map((a, k) => (
                  <button key={k} type="button" className="chat-suggestion"
                    onClick={() => send(a)} disabled={busy}>
                    <span className="chat-suggestion-icon">✦</span> {a}
                  </button>
                ))}
              </div>
            )}
            {msg.followUps && msg.followUps.length > 0 && (
              <div className="chat-followups">
                {msg.followUps.map((f, k) => (
                  <button key={k} type="button" className="chat-followup"
                    onClick={() => send(f)} disabled={busy}>{f}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {messages.length === 0 && (
        <div className="chat-suggestions">
          <div className="chat-suggestions-label">Try asking</div>
          {suggestions.map((s) => (
            <button key={s} type="button" className="chat-suggestion"
              onClick={() => send(s)} disabled={busy}>
              <span className="chat-suggestion-icon">✦</span> {s}
            </button>
          ))}
        </div>
      )}

      <form className="chat-input-row" onSubmit={(e) => { e.preventDefault(); send(input); }}>
        <input
          className="chat-input"
          aria-label="Ask about this report"
          placeholder="Ask about this report…"
          value={input}
          disabled={busy}
          onChange={(e) => setInput(e.target.value)}
        />
        <button type="submit" className="chat-send" disabled={busy || !input.trim()}>
          {busy ? '…' : 'Ask'}
        </button>
      </form>
    </div>
  );
}
